import React, { memo, useCallback, useMemo, useState } from 'react';
import { Box, Grid, ListItem, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { Duration } from 'luxon';
import { ClassNameMap } from '@mui/styles';
import { LabelOrigin, TimeSignal } from 'graphql/types';
import { DURATION_HH_MM_SS, formatTimeFromIsoString } from 'utils/date-time';
import { useMeContext } from 'modules/me';
import { useDialogState, useFeatureFlag } from 'modules/common/hooks';
import { TimesheetLabel } from 'modules/timesheet-labels/labelSchema';
import { TimesheetLabelSuggestionContextProvider } from 'modules/timesheet-labels';
import { useTrackRenderCounts } from 'modules/common/components/Render/hooks';
import { useIsSmartSuggestionSettingEnabled } from 'modules/timesheet-labels/hooks/useIsSmartSuggestionSettingEnabled';
import { CustomLabelsList } from 'modules/customLabels/components/CustomLabelsList';
import { useFormattedDuration } from 'modules/notes/common';
import { HandleTimeSingalActionCallback } from '../notesSelector/hooks';
import {
  NOTES_GROUP_BY_TYPE,
  useNotesFilterContext
} from '../NotesFilterContextProvider';
import TimeSignalQuickActionsByCategory from '../notesSelector/components/TimeSignalQuickActionsByCategory';
import { EditNoteDialog } from '../addNote';
import { SplitNoteDialog } from '../splitNote';
import { useNotesAnimationContext } from '../providers';
import { useAnimationInfo } from '../hooks';
import { HandleTimeSignalExcludeActionCallback } from '../notesSelector/hooks/useTimeSignalExcludeAction';
import { usePopoverState } from '../../common/hooks/usePopoverState';
import { NoteListItemCheckbox } from './NoteListItemCheckbox';
import { NoteClassificationTag } from './NoteClassificationTag';
import { NoteSourceGroup } from './NoteSourceGroup';
import { ListLabels } from './ListLabels';
import { GroupingTree } from './groupBy';
import { ExcludedTag } from './ExcludedTag';
import { useNotesListItemDrag } from './hooks/useNotesListItemDrag';
import { TimeSignalQuickActionsPopover } from './TimeSignalQuickActionsPopover';

export const mapDurationToDisplay = (duration: number): string =>
  Duration.fromObject({ seconds: duration }).toFormat(DURATION_HH_MM_SS);

export const NOTE_LIST_ITEM_HEIGHT = 7.5;

type StyleProps = {
  readOnly?: boolean;
  classes?: ClassNameMap;
  isItemGrouped: boolean;
  excluded: boolean;
  isTIWebGroupViewEnabled: boolean;
  isItemUncategorized: boolean;
  isItemScrolled: boolean;
};

export const useStyles = makeStyles((theme) => ({
  root: {
    '&:hover': {
      cursor: ({ readOnly }: StyleProps) => (!readOnly ? 'pointer' : 'default')
    },
    '&:hover $quickAction': {
      display: 'inherit'
    },
    '&:hover .MuiCheckbox-root': {
      color: 'rgba(0, 0, 0, 0.5)'
    },
    padding: theme.spacing(0),
    boxShadow: ({ isItemGrouped }: StyleProps) =>
      isItemGrouped ? undefined : 'inset 0 -1px 0 0 rgb(100 121 143 / 12%)',
    width: '100%',
    backgroundColor: 'inherit',
    '&:last-child $groupingTreeLastChild': {
      display: 'none'
    },
    '&:last-child $contentGridRoot': {
      boxShadow: 'none'
    }
  },
  box: {
    width: '100%',
    height: theme.spacing(NOTE_LIST_ITEM_HEIGHT),
    position: 'relative'
  },
  paper: {
    width: '100%',
    margin: theme.spacing(0),
    backgroundColor: 'transparent'
  },
  hover: {
    '&:hover': {
      backgroundColor: theme.palette.common.white,
      boxShadow: theme.shadows[2]
    }
  },
  gridRoot: {
    height: '100%',
    padding: ({ isItemUncategorized }: StyleProps) =>
      isItemUncategorized ? theme.spacing(0) : theme.spacing(0, 0, 0, 1)
  },
  contentGridRoot: {
    padding: ({ isItemGrouped }: StyleProps) =>
      theme.spacing(0.5, isItemGrouped ? 0 : 6, 0.5, 0),
    margin: ({
      isItemGrouped,
      isItemUncategorized,
      isItemScrolled
    }: StyleProps) =>
      theme.spacing(
        0,
        isItemScrolled && isItemUncategorized
          ? 3.75
          : isItemUncategorized
          ? 5.25
          : isItemGrouped
          ? 6.25
          : 0,
        0,
        0
      ),
    width: '100%',
    boxShadow: ({ isItemGrouped }: StyleProps) =>
      isItemGrouped ? 'inset 0 -1px 0 0 rgb(100 121 143 / 12%)' : undefined,
    userSelect: 'none'
  },
  contentRoot: {
    width: '100%'
  },
  content: {
    height: theme.spacing(2.5)
  },
  descriptionContainer: ({ isItemGrouped }: StyleProps) => {
    if (isItemGrouped) {
      return {
        display: 'flex',
        alignItems: 'center'
      };
    }
    return {};
  },
  description: {
    marginRight: theme.spacing(1),
    ...theme.typography.body2,
    flexGrow: ({ isItemGrouped }: StyleProps) => (isItemGrouped ? '1' : '0'),
    position: ({ isItemUncategorized, isItemGrouped }: StyleProps) =>
      isItemGrouped && !isItemUncategorized ? 'relative' : undefined,
    top: ({ isItemUncategorized, isItemGrouped }: StyleProps) =>
      isItemGrouped && !isItemUncategorized ? theme.spacing(2) : undefined
  },
  emptyNote: {
    fontStyle: 'italic',
    color: theme.palette.text.secondary
  },
  duration: {
    minWidth: theme.spacing(7),
    margin: theme.spacing(0),
    textAlign: 'right',
    flexShrink: 0
  },
  checkboxAndGroupingTree: {
    display: 'flex',
    alignItems: 'center'
  },
  source: {
    display: 'flex',
    alignItems: 'center',
    paddingRight: theme.spacing(1.25)
  },
  timeContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginTop: theme.spacing(-0.5)
  },
  time: {
    width: ({ isItemGrouped }: StyleProps) =>
      isItemGrouped ? theme.spacing(16) : theme.spacing(19),
    textAlign: 'right',
    marginRight: theme.spacing(0.75),
    marginLeft: theme.spacing(2),
    marginTop: theme.spacing(0.125),
    flexShrink: 0
  },
  tag: {
    minWidth: theme.spacing(7),
    margin: theme.spacing(-0.25, 0.25, 0.75, 0.75),
    textAlign: 'inherit'
  },
  quickAction: ({ isTIWebGroupViewEnabled }: StyleProps) => {
    const baseProperties = {
      opacity: 1,
      padding: theme.spacing(0.25)
    };

    if (isTIWebGroupViewEnabled) {
      return baseProperties;
    }

    return {
      display: 'none',
      position: 'absolute',
      right: theme.spacing(5),
      top: theme.spacing(-4.25),
      backgroundColor: theme.palette.common.white,
      boxShadow: theme.shadows[2],
      zIndex: 110,
      borderRadius: theme.spacing(2.5),
      ...baseProperties
    };
  },
  dragging: {
    opacity: 0.5
  },
  action: {
    color: theme.palette.grey[700],
    fill: theme.palette.grey[700],
    '&:hover': {
      color: theme.palette.primary.main,
      fill: theme.palette.primary.main
    }
  },
  tagRight: {
    display: 'flex',
    position: 'absolute',
    top: '71%',
    right: theme.spacing(6.25),
    transform: 'translateY(-67%)'
  },
  labelContainer: {
    display: 'flex',
    paddingTop: theme.spacing(0.25),
    overflow: 'hidden'
  },
  excluded: {
    opacity: ({ excluded, isTIWebGroupViewEnabled }: StyleProps) =>
      excluded && isTIWebGroupViewEnabled ? '0.5' : '1.0'
  },
  groupingTreeLastChild: {}, // This class is used via being passed down to sub-components
  groupingTreeContainer: {
    height: theme.spacing(NOTE_LIST_ITEM_HEIGHT),
    width: theme.spacing(3.5),
    padding: theme.spacing(0, 0.25, 0, 0.25),
    margin: theme.spacing(0, 1.125, 0, 0)
  },
  excludedTag: {
    marginTop: theme.spacing(0.75)
  }
}));

export const useAnimationStyles = makeStyles((theme) => ({
  Accepted: {
    backgroundColor: theme.palette.notesLabel.acceptedHalfOpacity,
    opacity: 0.5
  },
  Private: {
    backgroundColor: theme.palette.notesLabel.privateHalfOpacity,
    opacity: 0.5
  },
  Trash: {
    backgroundColor: theme.palette.notesLabel.trash,
    opacity: 0.5
  },
  Unclassified: {
    backgroundColor: theme.palette.notesLabel.inboxHalfOpacity,
    opacity: 0.5
  },
  DeleteForever: {
    backgroundColor: theme.palette.notesLabel.trash,
    opacity: 0.5
  },
  toast: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: theme.spacing(0.5),
    zIndex: 20,
    background: theme.palette.grey[900],
    color: theme.palette.common.white,
    opacity: 0.87,
    padding: theme.spacing(0.5, 1),
    textTransform: 'uppercase'
  },
  fadeOutAnim: {
    animation: '$fadeOutAnimation 0.5s',
    animationFillMode: 'forwards'
  },
  fadeInAnim: {
    animation: '$fadeInAnimation 1s'
  },
  '@keyframes fadeOutAnimation': {
    '0%': {
      opacity: 0.5
    },
    '30%': {
      opacity: 0.3
    },
    '50%': {
      opacity: 0.1
    },
    '80%': {
      opacity: 0.1
    },
    '96%': {
      height: '50px'
    },
    '98%': {
      height: '30px'
    },
    '100%': {
      opacity: 0,
      height: '10px'
    }
  },
  '@keyframes fadeInAnimation': {
    '0%': {
      opacity: 0,
      height: '10px'
    },
    '30%': {
      opacity: 0.1,
      height: '30px'
    },
    '50%': {
      opacity: 0.3
    },
    '80%': {
      opacity: 0.6
    },
    '100%': {
      opacity: 1
    }
  }
}));

const useFolderTagStyles = makeStyles((theme) => ({
  root: {
    minWidth: theme.spacing(9),
    margin: theme.spacing(0, 0, 0, 2),
    textAlign: 'inherit',
    display: 'flex',
    justifyContent: 'right'
  },
  label: {
    padding: theme.spacing(0, 0.75),
    marginTop: theme.spacing(0.25)
  }
}));

interface ListStyleProps {
  isItemGrouped: boolean;
}

export const useListStyles = makeStyles(() => ({
  root: {
    display: ({ isItemGrouped }: ListStyleProps) =>
      isItemGrouped ? 'flex' : 'block',
    alignItems: ({ isItemGrouped }: ListStyleProps) =>
      isItemGrouped ? 'center' : undefined
  }
}));

interface CustomTagRootStyleProps {
  excluded: boolean;
  isTIWebGroupViewEnabled: boolean;
}

const useCustomTagRootSyles = makeStyles((theme) => ({
  root: {
    alignItems: 'center',
    justifyContent: 'flex-end',
    margin: theme.spacing(0, 0, 0.25, 0),
    opacity: ({ excluded, isTIWebGroupViewEnabled }: CustomTagRootStyleProps) =>
      excluded && isTIWebGroupViewEnabled ? '0.5' : '1.0'
  }
}));

export type NoteItemProps = {
  note: TimeSignal;
  handleTimeSignalAction?: HandleTimeSingalActionCallback;
  isActionInProgress?: boolean;
  handleTimeSignalExcludeAction?: HandleTimeSignalExcludeActionCallback;
  isExcludeActionInProgress?: boolean;
  readOnly?: boolean;
  isItemGrouped?: boolean;
  isItemUncategorized?: boolean;
  isItemScrolled?: boolean;
  classes?: ClassNameMap;
  groupIndex?: string;
};

export const NoteListItem: React.FC<NoteItemProps> = memo(
  ({
    note,
    handleTimeSignalAction,
    isActionInProgress,
    handleTimeSignalExcludeAction,
    isExcludeActionInProgress,
    readOnly = false,
    isItemGrouped = false,
    isItemUncategorized = false,
    isItemScrolled = false,
    classes: overrideClasses,
    groupIndex
  }: NoteItemProps) => {
    const animationStyles = useAnimationStyles();
    const { hasAnimation } = useNotesAnimationContext();
    const animationData = hasAnimation(note.id);
    const isTIWebGroupViewEnabledFF: boolean = useFeatureFlag(
      'isTIWebGroupViewEnabled',
      false
    );

    const { filterContext } = useNotesFilterContext();

    const isTIWebGroupViewEnabled =
      isTIWebGroupViewEnabledFF &&
      filterContext.groupBy === NOTES_GROUP_BY_TYPE.LABELS;

    const classes = useStyles({
      readOnly,
      classes: overrideClasses,
      isItemGrouped,
      excluded: note.excluded ?? false,
      isTIWebGroupViewEnabled: isTIWebGroupViewEnabled,
      isItemScrolled: isItemScrolled,
      isItemUncategorized: isItemUncategorized
    });
    const quickActionClasses = useMemo(
      () => ({
        root: classes.quickAction,
        actions: classes.action
      }),
      [classes.action, classes.quickAction]
    );

    const {
      open: openEditNote,
      openDialog: openEditNoteDialog,
      closeDialog: closeEditNoteDialog
    } = useDialogState();
    const {
      open: isSplitNoteDialogOpen,
      openDialog: openSplitNoteDialog,
      closeDialog: closeSplitNoteDialog
    } = useDialogState();
    const { doFadeOutAnimation, doFadeInAnimation } = useAnimationInfo(
      animationData
    );

    const { isDragging, dragRef, useMeasureRef } = useNotesListItemDrag({
      note,
      groupIndex: groupIndex ?? '',
      isItemUncategorized,
      isItemGrouped,
      isItemScrolled
    });

    const {
      anchorEl,
      isPopoverOpen,
      onPopoverOpen,
      onPopoverClose
    } = usePopoverState();

    // render quick action on mouse over to improve initial page load performance
    const isMyWorkPerformanceEnabled = useFeatureFlag(
      'isMyWorkPerformanceEnabled',
      false
    );
    const [renderQuickAction, setRenderQuickAction] = useState(
      !isMyWorkPerformanceEnabled
    );
    const renderQuickActions = useCallback(() => {
      if (isMyWorkPerformanceEnabled) setRenderQuickAction(true);
    }, [isMyWorkPerformanceEnabled]);

    return (
      <>
        <ListItem
          ref={dragRef}
          data-testid="note-list-item"
          onClick={openEditNoteDialog}
          onMouseEnter={renderQuickActions}
          onMouseOver={onPopoverOpen}
          onMouseLeave={onPopoverClose}
          classes={useListStyles({ isItemGrouped: isItemGrouped })}
          className={classnames(
            classes.root,
            classes.paper,
            isDragging ? classes.dragging : undefined,
            !readOnly && classes.hover,
            {
              [animationStyles.fadeOutAnim]:
                doFadeOutAnimation &&
                ((filterContext.selectedClassification === 'All' &&
                  animationData?.folder === 'Trash') ||
                  (filterContext.selectedClassification !== 'All' &&
                    animationData?.action)),
              [animationStyles.fadeInAnim]: doFadeInAnimation
            }
          )}
        >
          {renderQuickAction &&
            !readOnly &&
            !isDragging &&
            handleTimeSignalAction &&
            handleTimeSignalExcludeAction &&
            (isTIWebGroupViewEnabled ? (
              <TimeSignalQuickActionsPopover
                onPopoverOpen={onPopoverOpen}
                onPopoverClose={onPopoverClose}
                anchorEl={anchorEl}
                isPopoverOpen={isPopoverOpen}
              >
                <TimeSignalQuickActionsByCategory
                  filterClassification={filterContext.selectedClassification}
                  note={note}
                  handleTimeSignalAction={handleTimeSignalAction}
                  loading={isActionInProgress}
                  classes={quickActionClasses}
                  onSplitAction={openSplitNoteDialog}
                  handleTimeSignalExcludeAction={handleTimeSignalExcludeAction}
                  excludeActionLoading={isExcludeActionInProgress}
                />
              </TimeSignalQuickActionsPopover>
            ) : (
              <TimeSignalQuickActionsByCategory
                filterClassification={filterContext.selectedClassification}
                note={note}
                handleTimeSignalAction={handleTimeSignalAction}
                loading={isActionInProgress}
                classes={quickActionClasses}
                onSplitAction={openSplitNoteDialog}
                handleTimeSignalExcludeAction={handleTimeSignalExcludeAction}
                excludeActionLoading={isExcludeActionInProgress}
              />
            ))}
          <Box className={classes.box} ref={useMeasureRef}>
            <Grid
              container
              data-testid={`note-list-item-grid-container-${note.id}`}
              className={classnames(
                classes.gridRoot,
                animationData?.folder && animationStyles[animationData?.folder]
              )}
              wrap="nowrap"
            >
              <Grid item className={classes.checkboxAndGroupingTree}>
                {!readOnly && <NoteListItemCheckbox note={note} />}
                {isItemGrouped && (
                  <div className={classes.groupingTreeContainer}>
                    {!isItemUncategorized && <GroupingTree classes={classes} />}
                  </div>
                )}
              </Grid>
              <NoteListItemContent
                note={note}
                readOnly={readOnly}
                isItemUncategorized={isItemUncategorized}
                isItemGrouped={isItemGrouped}
                classes={classes}
              />
            </Grid>
            {animationData?.action === 'Move' && (
              <Typography
                variant="body2"
                className={animationStyles.toast}
                data-testid={`note-item-${note.id}-action-toast`}
              >
                <FormattedMessage
                  id={`toast.timeEntryAnimation.${animationData?.folder}`}
                />
              </Typography>
            )}
          </Box>
        </ListItem>
        {openEditNote && !readOnly && (
          <TimesheetLabelSuggestionContextProvider>
            <EditNoteDialog
              open={openEditNote}
              onClose={closeEditNoteDialog}
              timeSignal={note}
            />
          </TimesheetLabelSuggestionContextProvider>
        )}

        {isSplitNoteDialogOpen && (
          <SplitNoteDialog
            open={isSplitNoteDialogOpen}
            onClose={closeSplitNoteDialog}
            timeSignal={note}
          />
        )}
      </>
    );
  }
);

NoteListItem.displayName = 'NoteListItem';

type NoteListItemContentProps = {
  note: TimeSignal;
  readOnly: boolean;
  isItemUncategorized: boolean;
  isItemGrouped: boolean;
  classes: ClassNameMap;
};
export const NoteListItemContent: React.FC<NoteListItemContentProps> = memo(
  ({
    note,
    note: { description, startTime, endTime },
    readOnly,
    isItemUncategorized,
    isItemGrouped,
    classes
  }: NoteListItemContentProps) => {
    useTrackRenderCounts('note-list-item', description);
    const isSmartSuggestionEnabled = useIsSmartSuggestionSettingEnabled();
    const isTIWebGroupViewEnabled: boolean = useFeatureFlag(
      'isTIWebGroupViewEnabled',
      false
    );
    const { filterContext } = useNotesFilterContext();
    const { hasStartEndTime, isAllFolder } = useMemo(
      () => ({
        hasStartEndTime: startTime && endTime,
        isAllFolder: filterContext.selectedClassification === 'All'
      }),
      [endTime, filterContext.selectedClassification, startTime]
    );

    const classesCustomTagRoot = useCustomTagRootSyles({
      excluded: note.excluded ?? false,
      isTIWebGroupViewEnabled: isTIWebGroupViewEnabled
    });
    const folderTagClasses = useFolderTagStyles();

    const me = useMeContext();
    const formattedDuration = useFormattedDuration({
      needsMask: true,
      duration: note.duration ?? 0
    });

    const timesheetLabels = useMemo(
      () =>
        note.labelValues
          ?.filter((l) => l.label?.origin !== LabelOrigin.User)
          .map((l) => {
            const retVal: TimesheetLabel = {
              ...l.label,
              value: l.value
            };
            if (isSmartSuggestionEnabled) {
              retVal.valueSource = l.valueSource;
            }
            return retVal;
          }),
      [isSmartSuggestionEnabled, note.labelValues]
    );
    const customLabels = useMemo(
      () =>
        note.labelValues
          ?.filter((l) => l.label?.origin === LabelOrigin.User && l.value)
          .map((l) => {
            const retVal: TimesheetLabel = {
              ...l.label,
              value: l.value
            };
            if (isSmartSuggestionEnabled) {
              retVal.valueSource = l.valueSource;
            }
            return retVal;
          }),
      [isSmartSuggestionEnabled, note.labelValues]
    );

    const gridSizes = useMemo(() => {
      if (customLabels.length) {
        return {
          xs: { tl: 6, cl: 6 },
          sm: { tl: 6, cl: 6 },
          md: { tl: 7, cl: 5 },
          lg: { tl: 8, cl: 4 }
        };
      }
      return {
        xs: { tl: 12, cl: 0 },
        sm: { tl: 12, cl: 0 },
        md: { tl: 12, cl: 0 },
        lg: { tl: 12, cl: 0 }
      };
    }, [customLabels.length]);

    return (
      <Grid
        container
        zeroMinWidth
        wrap="nowrap"
        data-testid={`note-list-item-grid-${note.id}`}
        className={classnames(classes.contentGridRoot)}
      >
        <Grid item className={classes.source}>
          <NoteSourceGroup
            sources={note.sources}
            className={classes.excluded}
          />
        </Grid>
        <Grid item zeroMinWidth className={classes.contentRoot}>
          <Grid
            container
            justifyContent="space-between"
            className={classes.content}
          >
            <Grid
              className={classnames(
                classes.descriptionContainer,
                classes.excluded
              )}
              item
              lg={isItemGrouped ? 7 : 8}
              md={isItemGrouped || isAllFolder ? 7 : 8}
              sm={isItemGrouped ? 7 : 7}
              xs={isItemGrouped ? 7 : 6}
            >
              <Typography
                data-testid="note"
                variant="body1"
                noWrap
                className={classnames(
                  classes.description,
                  note.description ? null : classes.emptyNote
                )}
              >
                {note.description || <FormattedMessage id="note.emptyNote" />}
              </Typography>
            </Grid>
            <Grid
              item
              lg={isItemGrouped ? 5 : 4}
              md={isItemGrouped || isAllFolder ? 5 : 4}
              sm={isItemGrouped ? 5 : 5}
              xs={isItemGrouped ? 5 : 6}
              className={classes.timeContainer}
              data-testid="timeContainer"
            >
              {isTIWebGroupViewEnabled && note.excluded && (
                <div className={classes.excludedTag}>
                  <ExcludedTag
                    note={note}
                    isItemGrouped={isItemGrouped}
                  ></ExcludedTag>
                </div>
              )}
              {(isAllFolder || readOnly) && (
                <NoteClassificationTag
                  noteClassification={note.classification}
                  classes={folderTagClasses}
                />
              )}
              <Typography
                data-testid="start-and-end-time"
                className={classnames(classes.time, classes.excluded)}
                variant={isItemGrouped ? 'caption' : 'body2'}
                noWrap
              >
                {formatTimeFromIsoString(startTime, me)}
                {hasStartEndTime && <>&nbsp; &ndash; &nbsp;</>}
                {formatTimeFromIsoString(endTime, me)}
              </Typography>
              <Typography
                data-testid="duration"
                variant={isItemGrouped ? 'caption' : 'body1'}
                className={classnames(classes.duration, classes.excluded)}
              >
                {note.duration ? formattedDuration : ''}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            wrap="nowrap"
            justifyContent="space-between"
            className={classes.excluded}
          >
            <Grid item xs={12} md={12}>
              <Grid container wrap="nowrap">
                {(!isItemGrouped || isItemUncategorized) && (
                  <Grid
                    container
                    xs={gridSizes.xs.tl}
                    sm={gridSizes.sm.tl}
                    md={gridSizes.md.tl}
                    lg={gridSizes.lg.tl}
                  >
                    <ListLabels labels={timesheetLabels} />
                  </Grid>
                )}
                {Boolean(customLabels.length) && (
                  <Grid
                    container
                    xs={gridSizes.xs.cl}
                    sm={gridSizes.sm.cl}
                    md={gridSizes.md.cl}
                    lg={gridSizes.lg.cl}
                  >
                    <CustomLabelsList
                      labels={customLabels}
                      classes={classesCustomTagRoot}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  }
);

NoteListItemContent.displayName = 'NoteListItemContent';
